import React, { useState, useEffect } from 'react';
import { FaBell } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { waiterAPI, restaurantAPI } from '../utils/api';
import { useRestaurantId } from '../utils/restaurantContext';
import WifiRequiredPopup from '../components/WifiRequiredPopup';
import './CallWaiterButton.css';

function CallWaiterButton() {
  const navigate = useNavigate();
  const restaurantId = useRestaurantId();
  const [isCalling, setIsCalling] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableNumber, setTableNumber] = useState('');
  const [wifiPopupOpen, setWifiPopupOpen] = useState(false);
  const [wifiClientIp, setWifiClientIp] = useState('');
  const [isCheckingWifi, setIsCheckingWifi] = useState(false);
  const [maxTables, setMaxTables] = useState(null);

  const ensureWifiAllowed = async () => {
    if (!restaurantId) throw new Error('Restaurant not selected. Please scan the restaurant QR code.');

    const wifiRes = await restaurantAPI.getWifiStatus(restaurantId);
    setWifiClientIp(wifiRes?.data?.clientIp || '');
    if (!wifiRes?.data?.allowed) {
      setWifiPopupOpen(true);
      return false;
    }
    return true;
  };

  const submitCall = async () => {
    if (!tableNumber) {
      setNotificationMessage('Please enter your table number to call the waiter.');
      setShowNotification(true);
      return;
    }

    const parsedTable = Number(tableNumber);
    if (maxTables && (!Number.isInteger(parsedTable) || parsedTable < 1 || parsedTable > maxTables)) {
      setNotificationMessage(`Please enter a valid table number (1-${maxTables}).`);
      setShowNotification(true);
      return;
    }

    try {
      setIsCalling(true);

      // Pre-check WiFi status before calling waiter
      const ok = await ensureWifiAllowed();
      if (!ok) return;

      await waiterAPI.callWaiter({ restaurantId, tableNumber: maxTables ? Number(tableNumber) : parseInt(tableNumber, 10) });

      setNotificationMessage('Waiter has been notified! They will be at your table shortly.');
      setShowNotification(true);
      setShowTableModal(false);
      setTableNumber('');

      // Auto-hide after a few seconds
      setTimeout(() => {
        setShowNotification(false);
        setNotificationMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error calling waiter:', error);

      if (error?.status === 403) {
        // Backend blocked action due to WiFi-only mode.
        setWifiPopupOpen(true);
        return;
      }

      setNotificationMessage(error.message || 'Failed to call waiter. Please try again.');
      setShowNotification(true);
    } finally {
      setIsCalling(false);
    }
  };

  const handleSubmitCall = async (e) => {
    e.preventDefault();
    await submitCall();
  };

  // Fetch restaurant meta to enforce table range
  useEffect(() => {
    if (!restaurantId) {
      // No restaurant context -> don't call backend with missing id
      // and send user back to QR landing.
      navigate('/customer', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await restaurantAPI.getById(restaurantId);
        const tables = Number(res?.data?.restaurant?.number_of_tables);
        if (Number.isInteger(tables) && tables > 0) setMaxTables(tables);
      } catch {
        // ignore
      }
    })();
  }, [restaurantId, navigate]);

  return (
    <div className="call-waiter-wrapper">
      <WifiRequiredPopup
        isOpen={wifiPopupOpen}
        clientIp={wifiClientIp}
        onClose={() => setWifiPopupOpen(false)}
        onRetry={submitCall}
        actionLabel="Call the waiter"
      />
      {showNotification && notificationMessage && (
        <div className="call-waiter-notification">
          <span>{notificationMessage}</span>
          <button onClick={() => setShowNotification(false)} aria-label="Close notification">
            ×
          </button>
        </div>
      )}
      <button 
        className={`call-waiter-btn ${isCalling || isCheckingWifi ? 'calling' : ''}`}
        onClick={async () => {
          try {
            setIsCheckingWifi(true);
            const ok = await ensureWifiAllowed();
            if (!ok) return;
            setShowTableModal(true);
          } catch (e) {
            // If wifi-status check fails, allow user to proceed and let backend enforce.
            setShowTableModal(true);
          } finally {
            setIsCheckingWifi(false);
          }
        }}
        disabled={isCalling || isCheckingWifi}
      >
        <FaBell className="waiter-icon" />
        <span>{isCalling || isCheckingWifi ? 'Calling...' : 'Call Waiter'}</span>
      </button>

      {showTableModal && (
        <div className="call-waiter-modal-overlay" onClick={() => setShowTableModal(false)}>
          <div className="call-waiter-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Call Waiter</h3>
            <p className="call-waiter-modal-text">Please enter your table number so the waiter can find you.</p>
            <form onSubmit={handleSubmitCall} className="call-waiter-form">
              <input
                type="number"
                min="1"
                max={maxTables || undefined}
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                placeholder={maxTables ? `Table number (1-${maxTables})` : 'Table number'}
                className="call-waiter-input"
              />
              <div className="call-waiter-actions">
                <button
                  type="button"
                  className="call-waiter-cancel"
                  onClick={() => {
                    setShowTableModal(false);
                    setTableNumber('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="call-waiter-confirm" disabled={isCalling}>
                  {isCalling ? 'Calling...' : 'Confirm Call'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CallWaiterButton;

